import { Injectable, OnDestroy } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { ChatbotActions } from './chatbot.actions';
import { IConversation, IMessage } from '../models/chatbot.interfaces';
import { ChatbotGetConversationService } from '../services/chatbot-get-conversation.service';
import { ChatbotWebsocketService } from '../services/chatbot-websocket.service';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { EMPTY, Subscription, timer } from 'rxjs';
import { AuthActions } from '../../../../core/auth/state/auth.actions';
import {
  CHATBOT_TIME_CONFIG,
  ChatbotWebsocketConversationHelperService
} from '../services/chatbot-websocket-conversation.helper.service';
import { AuthState } from '../../../../core/auth/state/auth.state';

export interface ChatbotWSStateModel {
  chatbotState?: ChatbotStateModel | null;
}

export interface ChatbotStateModel {
  canShow: boolean;
  isMinimised: boolean;
  isFullScreen: boolean;
  isThinking: boolean;
  isWarningState: boolean;
  isNewChatState: boolean;
  hasUnreadMessages: boolean;
  isSidePanelOpen: boolean;
  isMobile: boolean;
  hasLoadedLatest: boolean;
  isConnected: boolean;
  latestError?: string | null;
  stream?: {
    active: boolean;
    messageId: number | null; // local UI placeholder id
    serverMessageId?: string | null; // server message id (uuid)
    finishing?: boolean;
    buffer: string;
    lastFlushTs: number;
    flushMs: number;
    nextSeq: number | null; // next expected sequence
    pending: Record<number, { content: string; timestamp: number }> | null;
    missingSince?: Record<number, number>;
    gapGraceMs?: number;
    pendingTimestamps: Record<number, number> | null; // Track when each chunk was added
  };
  conversationHistory: IConversation;
  uiMessageHistory: IMessage[];
}

@State<ChatbotWSStateModel>({
  name: 'chatbot',
  defaults: {
    chatbotState: {
      canShow: true,
      isMinimised: true,
      isFullScreen: false,
      isThinking: false,
      isWarningState: false,
      isNewChatState: false,
      hasUnreadMessages: true,
      isSidePanelOpen: false,
      isMobile: false,
      hasLoadedLatest: false,
      isConnected: false,
      stream: {
        active: false,
        messageId: null,
        serverMessageId: null,
        buffer: '',
        finishing: false,
        lastFlushTs: 0,
        flushMs: CHATBOT_TIME_CONFIG.FLUSH_MS,
        nextSeq: 0,
        pending: {},
        missingSince: {},
        pendingTimestamps: {},
        gapGraceMs: CHATBOT_TIME_CONFIG.GAP_GRACE_MS
      },
      conversationHistory: {
        conversationId: null,
        userId: null,
        createdDate: null,
        updatedDate: null,
        messages: []
      },
      uiMessageHistory: []
    }
  }
})
@Injectable()
export class ChatbotState implements OnDestroy {
  private streamDrainSub?: Subscription;
  private wsSubscription?: Subscription;

  constructor(
    private store: Store,
    private chatbotConversationService: ChatbotGetConversationService,
    private readonly websocketService: ChatbotWebsocketService,
    private readonly chatbotWebSocketConversationHelperService: ChatbotWebsocketConversationHelperService
  ) {}

  // Selectors
  @Selector()
  static chatbotState(s: ChatbotWSStateModel) {
    return s.chatbotState ?? null;
  }

  @Selector()
  static hasLoadedLatest(s: ChatbotWSStateModel) {
    return s.chatbotState.hasLoadedLatest ?? false;
  }

  @Selector()
  static isMinimised(s: ChatbotWSStateModel) {
    return s.chatbotState?.isMinimised ?? true;
  }

  @Selector()
  static canShowChatbot(s: ChatbotWSStateModel) {
    return s.chatbotState?.canShow ?? false;
  }

  @Selector()
  static isMobileView(s: ChatbotWSStateModel) {
    return s.chatbotState?.isMobile ?? false;
  }

  @Selector()
  static isFullScreen(s: ChatbotWSStateModel) {
    return s.chatbotState?.isFullScreen ?? false;
  }

  @Selector()
  static isWarningState(s: ChatbotWSStateModel) {
    return s.chatbotState?.isWarningState ?? false;
  }

  @Selector()
  static isNewChatState(s: ChatbotWSStateModel) {
    return s.chatbotState?.isNewChatState ?? false;
  }

  @Selector()
  static hasUnreadMessages(s: ChatbotWSStateModel) {
    return s.chatbotState?.hasUnreadMessages ?? false;
  }

  @Selector()
  static chatbotMessageHistory(s: ChatbotWSStateModel) {
    return s.chatbotState?.uiMessageHistory ?? [];
  }

  @Selector()
  static isChatbotThinking(s: ChatbotWSStateModel) {
    return s.chatbotState?.isThinking ?? false;
  }

  @Selector()
  static isStreaming(s: ChatbotWSStateModel): boolean {
    return !!s.chatbotState?.stream?.active;
  }

  @Selector()
  static isSidePanelActive(s: ChatbotWSStateModel) {
    return s.chatbotState?.isSidePanelOpen ?? false;
  }

  @Selector()
  static latestError(s: ChatbotWSStateModel) {
    return s.chatbotState?.latestError ?? null;
  }

  // Actions
  @Action(ChatbotActions.LoadLatest, { cancelUncompleted: true })
  loadLatest(ctx: StateContext<ChatbotWSStateModel>) {
    const s = this.getChild(ctx.getState());
    if (s.hasLoadedLatest) return;

    const canSeeChat = this.store.selectSnapshot(AuthState.canSeeChat);

    // Not allowed - hide immediately, never call API.
    if (!canSeeChat) {
      this.patchChild(ctx, {
        canShow: false,
        hasLoadedLatest: true,
        latestError: null,
        conversationHistory: { conversationId: null, userId: null, createdDate: null, updatedDate: null, messages: [] },
        uiMessageHistory: []
      });
      return;
    }

    // Show immediately, even if API is slow.
    if (!s.canShow) {
      this.patchChild(ctx, { canShow: true });
    }

    if (s.latestError) {
      ctx.dispatch(new ChatbotActions.HasErrorLoadingConversation(null));
    }

    return this.chatbotConversationService.getLatestConversation().pipe(
      tap((convo) => {
        ctx.dispatch(new ChatbotActions.Initialise(convo));
        this.patchChild(ctx, { hasLoadedLatest: true, canShow: true });
      }),
      catchError((err) => {
        console.error('[Chatbot] getLatestConversation failed', err);

        if (err?.status === 403) {
          // hide chatbot permanently if user is forbidden, this is a fallback in case it's missed earlier
          this.patchChild(ctx, { canShow: false, hasLoadedLatest: true, latestError: null });
        } else {
          // Keep it visible so user can retry / see error UI
          this.patchChild(ctx, {
            canShow: true,
            latestError: 'Failed to fetch conversation history, please try again.'
          });
        }

        return EMPTY;
      })
    );
  }

  @Action(ChatbotActions.HasErrorLoadingConversation)
  hasErrorsLoadingConversation(
    ctx: StateContext<ChatbotWSStateModel>,
    { latestError }: ChatbotActions.HasErrorLoadingConversation
  ) {
    this.patchChild(ctx, { latestError });
  }

  @Action(ChatbotActions.Initialise)
  init(ctx: StateContext<ChatbotWSStateModel>, { conversationObject }: ChatbotActions.Initialise) {
    if ((conversationObject?.messages?.length ?? 0) === 0) {
      ctx.dispatch(new ChatbotActions.NewChatUI(true));
    }

    ctx.patchState({
      chatbotState: {
        ...this.getChild(ctx.getState()),
        conversationHistory: conversationObject,
        uiMessageHistory: conversationObject.messages
      }
    });
  }

  @Action(ChatbotActions.ConnectWS)
  connectToWebSocket(ctx: StateContext<ChatbotWSStateModel>, { conversationObject }: ChatbotActions.ConnectWS) {
    // If already connected/subscribed, drop the old one first
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = undefined;

    this.wsSubscription = this.websocketService.connect$(conversationObject.conversationId).subscribe({
      next: (isConnected) => {
        this.patchChild(ctx, { isConnected });
      },
      error: (err) => {
        console.error('[WS] connection stream error', err);
        this.patchChild(ctx, { isConnected: false });
      },
      complete: () => {
        // only happens if service completes the stream
        this.patchChild(ctx, { isConnected: false });
      }
    });
  }

  @Action(ChatbotActions.DisconnectWS)
  disconnectWs(ctx: StateContext<ChatbotWSStateModel>) {
    // cancel our subscription
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = undefined;
    // service to close the socket
    this.websocketService.disconnect();
    this.patchChild(ctx, { isConnected: false });
  }

  @Action(ChatbotActions.StartNewWSConversation)
  startNew(ctx: StateContext<ChatbotWSStateModel>) {
    this.patchChild(ctx, {
      uiMessageHistory: [],
      conversationHistory: { conversationId: null, userId: null, createdDate: null, updatedDate: null, messages: [] }
    });

    return this.store.dispatch(new AuthActions.EnsureToken()).pipe(
      switchMap(() => this.chatbotConversationService.getNewConversation()),
      tap((conversation) => ctx.dispatch(new ChatbotActions.NewWSConversationReady(conversation)))
    );
  }

  @Action(ChatbotActions.NewWSConversationReady)
  onNewConversationReady(
    ctx: StateContext<ChatbotWSStateModel>,
    { conversation }: ChatbotActions.NewWSConversationReady
  ) {
    this.patchChild(ctx, {
      conversationHistory: conversation,
      uiMessageHistory: conversation.messages ?? []
    });
  }

  @Action(ChatbotActions.SendWSMessage)
  sendWsMessage(
    _: StateContext<ChatbotWSStateModel>,
    { conversationId, message, entityContextRoute }: ChatbotActions.SendWSMessage
  ) {
    return this.websocketService.connect$(conversationId).pipe(
      filter(Boolean),
      take(1),
      tap(() => this.websocketService.send(conversationId, message, entityContextRoute))
    );
  }

  // ----- actions for START / COMPLETE / ERROR -----
  @Action(ChatbotActions.WSStart)
  onWsStart(ctx: StateContext<ChatbotWSStateModel>, { serverMessageId }: ChatbotActions.WSStart) {
    const list = ctx.getState().chatbotState.uiMessageHistory;
    const maxId = list.length ? Math.max(...list.map((m) => m.id || 0)) : 0;
    const tempId = maxId + 1;

    const s = this.getChild(ctx.getState());

    this.patchChild(ctx, {
      uiMessageHistory: [
        ...s.uiMessageHistory,
        this.chatbotWebSocketConversationHelperService.createPlaceholderMessage(s.uiMessageHistory).placeholder
      ],
      stream: {
        active: true,
        messageId: tempId,
        serverMessageId,
        buffer: '',
        finishing: false,
        lastFlushTs: 0,
        flushMs: s.stream?.flushMs ?? CHATBOT_TIME_CONFIG.FLUSH_MS,
        nextSeq: 0,
        pending: {},
        missingSince: {},
        pendingTimestamps: {},
        gapGraceMs: CHATBOT_TIME_CONFIG.GAP_GRACE_MS
      },
      isThinking: true
    });

    this.startDrain(ctx);
  }

  @Action(ChatbotActions.WSChunk)
  onWsChunk(ctx: StateContext<ChatbotWSStateModel>, { data, seq, serverMessageId }: ChatbotActions.WSChunk) {
    if (seq == null || typeof seq !== 'number' || seq < 0 || seq > CHATBOT_TIME_CONFIG.MAX_SEQUENCE_NUMBER) {
      console.warn('Invalid sequence number:', seq, 'for message:', serverMessageId);
      return;
    }

    const s0 = this.getChild(ctx.getState());
    let st = s0.stream;

    data = this.chatbotWebSocketConversationHelperService.normalizeChunk(data ?? '');

    // Auto-start placeholder if needed
    if (!st?.active || !st.messageId) {
      const list = s0.uiMessageHistory;
      const maxId = list.length ? Math.max(...list.map((m) => m.id || 0)) : 0;
      const tempId = maxId + 1;

      this.patchChild(ctx, {
        uiMessageHistory: [
          ...list,
          this.chatbotWebSocketConversationHelperService.createPlaceholderMessage(list).placeholder
        ],
        stream: {
          active: true,
          messageId: tempId,
          serverMessageId,
          buffer: '',
          finishing: false,
          lastFlushTs: 0,
          flushMs: s0.stream?.flushMs ?? CHATBOT_TIME_CONFIG.FLUSH_MS,
          nextSeq: 0,
          pending: {},
          missingSince: {},
          gapGraceMs: CHATBOT_TIME_CONFIG.GAP_GRACE_MS,
          pendingTimestamps: {}
        },
        isThinking: true
      });
      this.startDrain(ctx);
      st = this.getChild(ctx.getState()).stream!;
    }

    // Wrong message guard
    if (st.serverMessageId && st.serverMessageId !== serverMessageId) return;

    const pending = { ...(st.pending || {}) };
    let nextSeq = st.nextSeq;
    const missingSince = { ...(st.missingSince || {}) };

    if (seq === nextSeq) {
      // in-order: append & drain any contiguous pending
      let buffer = (st.buffer || '') + (data || '');
      nextSeq++;
      while (pending[nextSeq] !== undefined) {
        buffer += pending[nextSeq]?.content || '';
        delete pending[nextSeq];
        nextSeq++;
      }
      // clear any missing markers
      for (const m in missingSince) {
        const mn = +m;
        if (mn < nextSeq) delete missingSince[mn];
      }
      this.patchChild(ctx, { stream: { ...st, buffer, pending, nextSeq, missingSince }, isThinking: false });
    } else if (seq != null && seq > nextSeq) {
      // out-of-order: record the gap and stash this chunk
      const now = Date.now();
      for (let m = nextSeq; m < seq; m++) {
        if (missingSince[m] == null) missingSince[m] = now;
      }

      // Create or update the pending chunk with the new structure
      pending[seq] = {
        content: data || '',
        timestamp: now
      };

      this.patchChild(ctx, { stream: { ...st, pending, missingSince }, isThinking: false });
    } else {
      // duplicate/late — ignore
    }

    // unread badge while minimised
    const s1 = this.getChild(ctx.getState());
    if (s1.isMinimised && !s1.hasUnreadMessages) {
      this.store.dispatch(new ChatbotActions.SetUnreadMessages(true));
    }
  }

  @Action(ChatbotActions.WSComplete)
  onWsComplete(ctx: StateContext<ChatbotWSStateModel>, { serverMessageId }: ChatbotActions.WSComplete) {
    const s = this.getChild(ctx.getState());
    const st = s.stream;
    if (!st?.active || !st.messageId) {
      this.patchChild(ctx, { isThinking: false });
      return;
    }

    // Check if we should error due to missing sequences
    if (this.chatbotWebSocketConversationHelperService.shouldErrorOnCompletion(st)) {
      // Handle as an error instead of completing normally
      this.upsertMessageById(ctx, st.messageId, {
        status: 'error',
        content: 'There was an error displaying your message, please try again'
      });

      this.patchChild(ctx, {
        isThinking: false,
        stream: {
          active: false,
          messageId: null,
          nextSeq: null,
          pending: {},
          serverMessageId: null,
          buffer: '',
          lastFlushTs: 0,
          flushMs: st.flushMs,
          pendingTimestamps: {}
        }
      });

      this.stopDrain();
      return;
    }

    this.patchChild(ctx, {
      stream: { ...st, finishing: true },
      isThinking: false
    });
  }

  @Action(ChatbotActions.WSError)
  onWsError(ctx: StateContext<ChatbotWSStateModel>, { error }: ChatbotActions.WSError) {
    this.stopDrain();
    const s = this.getChild(ctx.getState());
    const st = s.stream;

    if (st?.active && st.messageId) {
      this.upsertMessageById(ctx, st.messageId, { status: 'error' });
    }

    this.patchChild(ctx, {
      isThinking: false,
      stream: this.chatbotWebSocketConversationHelperService.createInitialStreamState(s.stream?.flushMs)
    });
    console.error('WS ERROR:', error);
  }

  @Action(ChatbotActions.AppendUiMessage)
  appendUiMessage(ctx: StateContext<ChatbotWSStateModel>, { message }: ChatbotActions.AppendUiMessage) {
    const s = this.getChild(ctx.getState());
    this.patchChild(ctx, { uiMessageHistory: [...s.uiMessageHistory, message] });
  }

  @Action(ChatbotActions.ToggleMinimise)
  toggleMinimise(ctx: StateContext<ChatbotWSStateModel>, { isMinimised }: ChatbotActions.ToggleMinimise) {
    this.patchChild(ctx, { isMinimised });
  }

  @Action(ChatbotActions.MobileTagView)
  isMobileView(ctx: StateContext<ChatbotWSStateModel>, { isMobile }: ChatbotActions.MobileTagView) {
    this.patchChild(ctx, { isMobile });
  }

  @Action(ChatbotActions.ToggleFullScreen)
  toggleFullScreen(ctx: StateContext<ChatbotWSStateModel>, { isFullScreen }: ChatbotActions.ToggleFullScreen) {
    this.patchChild(ctx, { isFullScreen });
  }

  @Action(ChatbotActions.WarningStateUI)
  setWarning(ctx: StateContext<ChatbotWSStateModel>, { isWarningState }: ChatbotActions.WarningStateUI) {
    this.patchChild(ctx, { isWarningState });
  }

  @Action(ChatbotActions.NewChatUI)
  setNewChat(ctx: StateContext<ChatbotWSStateModel>, { isNewChatState }: ChatbotActions.NewChatUI) {
    this.patchChild(ctx, { isNewChatState });
  }

  @Action(ChatbotActions.ToggleChatbotThinking)
  isChatbotThinking(ctx: StateContext<ChatbotWSStateModel>, { isThinking }: ChatbotActions.ToggleChatbotThinking) {
    this.patchChild(ctx, { isThinking });
  }

  @Action(ChatbotActions.SetUnreadMessages)
  setUnread(ctx: StateContext<ChatbotWSStateModel>, { hasUnreadMessages }: ChatbotActions.SetUnreadMessages) {
    this.patchChild(ctx, { hasUnreadMessages });
  }

  // Future requirement - leave in for now, this triggers different css classes so that the icon might move out of the sidepanel's way.
  @Action(ChatbotActions.SetChatbotIconWithSidePanel)
  isSidePanelActive(
    ctx: StateContext<ChatbotWSStateModel>,
    { isSidePanelOpen }: ChatbotActions.SetChatbotIconWithSidePanel
  ) {
    this.patchChild(ctx, { isSidePanelOpen });
  }

  @Action(ChatbotActions.Reset)
  reset(ctx: StateContext<ChatbotWSStateModel>) {
    // close sockets/timers
    this.stopDrain();
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = undefined;
    this.websocketService.disconnect();

    ctx.setState({
      chatbotState: {
        isMinimised: true,
        isFullScreen: false,
        isThinking: false,
        isWarningState: false,
        isNewChatState: false,
        hasUnreadMessages: true,
        isSidePanelOpen: false,
        isMobile: false,
        hasLoadedLatest: false,
        isConnected: false,
        latestError: null,
        canShow: false, // safe default, in case the next logged in user is NOT admin.
        stream: {
          active: false,
          messageId: null,
          serverMessageId: null,
          buffer: '',
          finishing: false,
          lastFlushTs: 0,
          flushMs: CHATBOT_TIME_CONFIG.FLUSH_MS,
          nextSeq: 0,
          pending: {},
          missingSince: {},
          pendingTimestamps: {},
          gapGraceMs: CHATBOT_TIME_CONFIG.GAP_GRACE_MS
        },
        conversationHistory: {
          conversationId: null,
          userId: null,
          createdDate: null,
          updatedDate: null,
          messages: []
        },
        uiMessageHistory: []
      }
    });
  }

  ngOnDestroy(): void {
    this.streamDrainSub?.unsubscribe();
    this.wsSubscription?.unsubscribe();
  }

  // HELPERS
  private getChild(state: ChatbotWSStateModel) {
    return state.chatbotState!;
  }

  private patchChild(ctx: StateContext<ChatbotWSStateModel>, partial: Partial<ChatbotStateModel>) {
    const child = this.getChild(ctx.getState());
    ctx.patchState({ chatbotState: { ...child, ...partial } });
  }

  private upsertMessageById(ctx: StateContext<ChatbotWSStateModel>, id: number, patch: Partial<IMessage>) {
    const s = this.getChild(ctx.getState());
    const message = s.uiMessageHistory.find((m) => m.id === id);
    if (!message) return;

    const updatedMessages = this.chatbotWebSocketConversationHelperService.upsertMessageById(
      message,
      s.uiMessageHistory,
      patch
    );
    this.patchChild(ctx, { uiMessageHistory: updatedMessages });
  }

  private appendToMessageContent(ctx: StateContext<ChatbotWSStateModel>, id: number, text: string) {
    const s = this.getChild(ctx.getState());
    const updatedMessages = this.chatbotWebSocketConversationHelperService.appendToMessageContent(
      id,
      s.uiMessageHistory,
      text
    );
    this.patchChild(ctx, { uiMessageHistory: updatedMessages });
  }

  private startDrain(ctx: StateContext<ChatbotWSStateModel>) {
    this.stopDrain();
    this.streamDrainSub = timer(0, CHATBOT_TIME_CONFIG.TICK_MS).subscribe(() => {
      // Clean up old pending chunks periodically
      let s = this.getChild(ctx.getState());
      let st = s.stream;
      if (!st?.active || !st.messageId) return;

      // Process cleanup
      const cleanupResult = this.chatbotWebSocketConversationHelperService.cleanupOldPendingChunks(st);
      if (cleanupResult.changed) {
        this.patchChild(ctx, {
          stream: { ...st, pending: cleanupResult.pending, missingSince: cleanupResult.missingSince }
        });
        // Refresh references
        s = this.getChild(ctx.getState());
        st = s.stream!;
      }

      // Process stream chunks
      const processResult = this.chatbotWebSocketConversationHelperService.processStreamChunks(st);
      if (processResult.changed) {
        this.patchChild(ctx, {
          stream: {
            ...st,
            buffer: processResult.buffer,
            nextSeq: processResult.nextSeq,
            pending: processResult.pending,
            missingSince: processResult.missingSince
          }
        });
        // Refresh references
        s = this.getChild(ctx.getState());
        st = s.stream!;
      }

      const bufNow = processResult.changed ? processResult.buffer : st.buffer || '';
      if (bufNow.length) {
        const slice = bufNow.slice(0, CHATBOT_TIME_CONFIG.STEP_CHARS);
        const rest = bufNow.slice(CHATBOT_TIME_CONFIG.STEP_CHARS);
        this.appendToMessageContent(ctx, st.messageId, slice);
        this.patchChild(ctx, { stream: { ...st, buffer: rest } });
        return;
      }

      const stillWaitingForGap = !!(st.missingSince && st.nextSeq !== null && st.missingSince[st.nextSeq]);
      if (st.finishing && !stillWaitingForGap) {
        this.upsertMessageById(ctx, st.messageId, { status: 'delivered' });
        this.patchChild(ctx, {
          stream: {
            active: false,
            serverMessageId: null,
            nextSeq: 0,
            pending: {},
            messageId: null,
            pendingTimestamps: {},
            buffer: '',
            lastFlushTs: 0,
            flushMs: st.flushMs
          }
        });
        this.stopDrain();
        this.store.dispatch(new ChatbotActions.DisconnectWS());
      }
    });
  }

  private stopDrain() {
    this.streamDrainSub?.unsubscribe();
    this.streamDrainSub = undefined;
  }
}
